import "./CustomerCard.scss";

import React, { useMemo } from "react";
import {
  faBirthdayCake,
  faCalendarCheck,
  faCircleInfo,
  faCrown,
  faEdit,
  faPhone,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatPhone, openWhatsApp } from "../../../commons/util/HallAppUtil";

import AppCard from "../../../commons/cards/AppCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

function ActionPillButton({
  color,
  borderColor,
  icon,
  label,
  onClick,
  title,
  disabled,
  loading,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled || loading}
      className="club-pill-btn"
      style={{
        color: color || "#111827",
        borderColor: borderColor || "#e8e8f0",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {loading ? (
        <span className="club-pill-spinner" aria-hidden="true" />
      ) : (
        <FontAwesomeIcon icon={icon} />
      )}
      {label}
    </button>
  );
}

const CustomerCard = ({
  customer,
  backgroundColor = "#fff",
  setSelectedCustomer,
  setModalHistoryCustomerShow,
  onEdit,
  onDelete,
}) => {
  const { name, whatsapp, birthday, lastVisit } = customer || {};

  const hasWhatsapp = Boolean(whatsapp);
  const formattedBirthday = birthday || "—";
  const formattedLastVisit = lastVisit ? formatDate(lastVisit) : "—";
  const formattedPhone = hasWhatsapp ? formatPhone(whatsapp) : "—";
  const isClubMember = customer?.hasClub === true;

  const { isBirthdayToday, isBirthdayThisMonth } = useMemo(() => {
    if (!birthday || typeof birthday !== "string" || !birthday.includes("/")) {
      return { isBirthdayToday: false, isBirthdayThisMonth: false };
    }

    const parts = birthday.split("/");
    const day = Number(parts[0]);
    const month = Number(parts[1]);
    if (!day || !month) return { isBirthdayToday: false, isBirthdayThisMonth: false };

    const today = new Date();
    const todayDay = today.getDate();
    const todayMonth = today.getMonth() + 1;

    return {
      isBirthdayToday: day === todayDay && month === todayMonth,
      isBirthdayThisMonth: month === todayMonth,
    };
  }, [birthday]);

  const openHistory = () => {
    setSelectedCustomer?.(customer);
    setModalHistoryCustomerShow?.(true);
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const initials = (name || "C")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const cardClassName =
    isBirthdayToday
      ? "hallapp-customer-card--birthday-today"
      : isBirthdayThisMonth
        ? "hallapp-customer-card--birthday-month"
        : "";

  return (
    <AppCard backgroundColor={backgroundColor}>
      <div className={`hallapp-customer-card ${cardClassName}`}>
        {/* TOP ACTIONS (editar / excluir) */}
        <div className="hallapp-customer-card__topActions">
          <ActionPillButton
            icon={faEdit}
            label="Editar"
            onClick={(e) => {
              stop(e);
              onEdit?.(customer);
            }}
            color="#1d4ed8"
            borderColor="#dbeafe"
            title="Editar cliente"
          />

          <ActionPillButton
            icon={faTrash}
            label="Excluir"
            onClick={(e) => {
              stop(e);
              onDelete?.(customer);
            }}
            color="#b42318"
            borderColor="#ffd6d6"
            title="Excluir cliente"
          />
        </div>

        {/* Header */}
        <div className="hallapp-customer-card__top">
          <div className="hallapp-customer-card__avatar" aria-hidden="true">
            {initials}
          </div>

          <div className="hallapp-customer-card__title">
            <div className="hallapp-customer-card__name" title={name || "Cliente"}>
              {name || "Cliente"}
              {isClubMember && (
                <span
                  className="hallapp-club-badge ms-2"
                  title="Cliente do clube"
                  aria-label="Cliente do clube"
                >
                  <FontAwesomeIcon icon={faCrown} />
                </span>
              )}

              {isBirthdayToday && (
                <span className="hallapp-badge hallapp-badge--today ms-2" title="Aniversário hoje">
                  🎉 NIVER HOJE
                </span>
              )}
              {!isBirthdayToday && isBirthdayThisMonth && (
                <span className="hallapp-badge hallapp-badge--month ms-2" title="Aniversário neste mês">
                  🎂 NIVER DO MÊS
                </span>
              )}
            </div>

            {hasWhatsapp ? (
              <div className="hallapp-customer-card__subtitle" title={formattedPhone}>
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                {formattedPhone}
              </div>
            ) : (
              <div className="hallapp-customer-card__subtitle text-muted">
                <FontAwesomeIcon icon={faPhone} className="me-2" />
                Sem WhatsApp cadastrado
              </div>
            )}
          </div>
        </div>

        {/* ACTIONS: sempre embaixo */}
        <div className="hallapp-customer-card__actionsRow">
          {hasWhatsapp && (
            <button
              type="button"
              className="btn btn-outline-success hallapp-customer-card__btn hallapp-customer-card__btn--full"
              onClick={(e) => {
                stop(e);
                openWhatsApp(whatsapp);
              }}
              onMouseDown={stop}
              onTouchStart={stop}
            >
              <FontAwesomeIcon icon={faWhatsapp} />
              <span className="ms-2">WhatsApp</span>
            </button>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary hallapp-customer-card__btn hallapp-customer-card__btn--full"
            onClick={(e) => {
              stop(e);
              openHistory();
            }}
            onMouseDown={stop}
            onTouchStart={stop}
          >
            <FontAwesomeIcon icon={faCircleInfo} />
            <span className="ms-2">Detalhes</span>
          </button>

          {hasWhatsapp && isBirthdayToday && (
            <button
              type="button"
              className="btn hallapp-birthday-cta hallapp-customer-card__btn hallapp-customer-card__btn--full"
              onClick={(e) => {
                stop(e);
                openWhatsApp(whatsapp, `🎉 Parabéns ${name || ""}! Que seu dia seja incrível 💜`);
              }}
              onMouseDown={stop}
              onTouchStart={stop}
            >
              <FontAwesomeIcon icon={faBirthdayCake} />
              <span className="ms-2">Parabenizar</span>
            </button>
          )}
        </div>

        {/* Info grid */}
        <div className="hallapp-customer-card__grid">
          <div className="hallapp-customer-card__chip">
            <FontAwesomeIcon icon={faBirthdayCake} className="hallapp-customer-card__chipIcon" />
            <div className="hallapp-customer-card__chipText">
              <div className="hallapp-customer-card__chipLabel">Aniversário</div>
              <div className="hallapp-customer-card__chipValue">
                {formattedBirthday}
                {isBirthdayToday ? " 🎉" : isBirthdayThisMonth ? " 🎂" : ""}
              </div>
            </div>
          </div>

          <div className="hallapp-customer-card__chip">
            <FontAwesomeIcon icon={faCalendarCheck} className="hallapp-customer-card__chipIcon" />
            <div className="hallapp-customer-card__chipText">
              <div className="hallapp-customer-card__chipLabel">Última visita</div>
              <div className="hallapp-customer-card__chipValue">{formattedLastVisit}</div>
            </div>
          </div>
        </div>
      </div>
    </AppCard>
  );
};

export default CustomerCard;
